import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  IsLoadingContactInfo,
  load,
  SelectActualClientId,
  SelectClientContactProfile,
  setIsLoadingContactInfo,
} from "../redux/reducers/clientContactProfile.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import {
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import { useNavigate } from "react-router";
import {
  IsClientsLoaded,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
} from "../redux/reducers/clientsList.reducer";
import {
  IsLoadingDropdownInfo,
  loadDropdownInfo,
  SelectDropdownsInfo,
  setIsLoadingDropdownInfo,
} from "../redux/reducers/clientDropdownsInfo.reducer";
import { checkContactIsInClientList } from "../utils/clientsUtils";
import { Client } from "../types/clients-module-types";

import { useClientsList } from "./useClientsList";

export const useClientContactProfile = (
  clientId: number,
  contactId: number
) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);
  const isLoadingContactInfo = useSelector(IsLoadingContactInfo);
  const actualClientId = useSelector(SelectActualClientId);
  const contactProfile = useSelector(SelectClientContactProfile);
  const dropdownsValues = useSelector(SelectDropdownsInfo);
  const isLoadingDropdownInfo = useSelector(IsLoadingDropdownInfo);

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  const loadContactProfile = useCallback(
    async (contactId) => {
      if (rolesLoaded && isClientsLoaded) {
        // This check should go in all clients custom hooks
        if (!clientsIds.includes(clientId)) {
          dispatch(
            addToast({
              mode: "error",
              message: "You're not allowed to see this Client Page",
            })
          );
          navigate(ROUTE_FORBIDDEN_PAGE);
          return true;
        }
        // Load info header when user put a new URL
        if (
          !ClientForHeaderInfo ||
          (ClientForHeaderInfo && ClientForHeaderInfo.id !== clientId)
        ) {
          dispatch(setIsActualClientForHeaderInfoLoading(true));
          let headerInfo = await ClientsService.getClientModuleHeaderInfo(
            clientId
          )
            .then((response) => {
              if (!response.success) {
                dispatch(
                  addToast({
                    mode: "error",
                    message: response.message,
                  })
                );
                dispatch(setIsActualClientForHeaderInfoLoading(false));
                navigate(ROUTE_CLIENTS);
                return true;
              } else {
                return response.data;
              }
            })
            .catch(() => {
              dispatch(
                addToast({
                  mode: "error",
                  message:
                    "Something wrong happened while getting the client header information.",
                })
              );
              dispatch(setIsActualClientForHeaderInfoLoading(false));
              navigate(ROUTE_CLIENTS);
              return true;
            });
          dispatch(setActualClientForHeaderInfo(headerInfo));
        }
        // dropdowns info
        let dropdownsInfo;
        if (!dropdownsValues) {
          dispatch(setIsLoadingDropdownInfo(true));
          dropdownsInfo = await ClientsService.getClientsDropdownsInfo()
            .then((data) => {
              return data;
            })
            .catch(() => {
              dispatch(
                addToast({
                  mode: "error",
                  message:
                    "Something wrong happened while getting dropdowns page information.",
                })
              );
              dispatch(setIsLoadingDropdownInfo(false));
              navigate(ROUTE_CLIENTS);
              return true;
            });
          dispatch(loadDropdownInfo(dropdownsInfo.data));
        }
        // page info
        dispatch(setIsLoadingContactInfo(true));
        const contactsInfo = await ClientsService.getClientContactProfile(
          contactId
        )
          .then((data) => {
            if (
              !data.success ||
              !checkContactIsInClientList(data.data.clients, clientId)
            ) {
              dispatch(
                addToast({
                  mode: "error",
                  message: !data.success
                    ? "Contact not found"
                    : "This Contact is not in this Client",
                })
              );
              dispatch(setIsLoadingContactInfo(false));
              navigate(ROUTE_CLIENTS);
              return true;
            } else {
              return data;
            }
          })
          .catch(() => {
            dispatch(
              addToast({
                mode: "error",
                message: "Contact not found",
              })
            );
            dispatch(setIsLoadingContactInfo(false));
            navigate(ROUTE_CLIENTS);
            return true;
          });
        dispatch(
          load({
            contactsInfo: contactsInfo.data,
            clientId: clientId,
          })
        );
      }
    },
    [dispatch, rolesLoaded, isClientsLoaded, ClientForHeaderInfo, dropdownsValues]
  );

  useEffect(() => {
    loadContactProfile(contactId);
  }, [loadContactProfile]);

  return {
    ClientForHeaderInfo,
    isLoadingContactInfo,
    isLoadingDropdownInfo,
    actualClientId,
    contactProfile,
    userRolesList,
    rolesLoaded,
    dropdownsValues,
  };
};
